import {
  Bell, AlertTriangle, AlertCircle, Info, CheckCircle2, Clock,
  Eye, Trash2, Settings, Filter, Check, CheckCheck
} from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { timeAgo } from '../../utils/helpers';

export default function NotificationsPage() {
  const { notifications, markNotificationRead, markAllRead, unreadCount } = useAppStore();

  const getIcon = (type: string) => {
    switch (type) {
      case 'alert': return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'success': return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      default: return <Info className="w-5 h-5 text-sky-500" />;
    }
  };

  const getBg = (type: string) => {
    switch (type) {
      case 'alert': return 'bg-red-50';
      case 'warning': return 'bg-amber-50';
      case 'success': return 'bg-emerald-50';
      default: return 'bg-sky-50';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Notifications</h1>
          <p className="text-sm text-slate-500 mt-1">{unreadCount} unread notifications</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={markAllRead} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2 transition-colors">
            <CheckCheck className="w-4 h-4" /> Mark All Read
          </button>
          <button className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 flex items-center gap-2 transition-colors">
            <Settings className="w-4 h-4" /> Preferences
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {notifications.map(notif => (
          <div
            key={notif.id}
            onClick={() => markNotificationRead(notif.id)}
            className={`bg-white rounded-2xl border p-4 flex items-start gap-4 cursor-pointer transition-all hover:shadow-md ${
              !notif.isRead ? 'border-l-4 border-l-sky-500 shadow-sm' : 'border-slate-100'
            }`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getBg(notif.type)}`}>
              {getIcon(notif.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h4 className={`text-sm ${!notif.isRead ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                  {notif.title}
                </h4>
                {!notif.isRead && <div className="w-2.5 h-2.5 rounded-full bg-sky-500 flex-shrink-0 mt-1" />}
              </div>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">{notif.message}</p>
              <p className="text-xs text-slate-400 mt-2 flex items-center gap-1.5">
                <Clock className="w-3 h-3" /> {timeAgo(notif.timestamp)}
                <span className="text-slate-300">·</span>
                <span className="capitalize">{notif.type}</span>
              </p>
            </div>
            <div className="flex gap-1 opacity-0 group-hover:opacity-100">
              {!notif.isRead && (
                <button className="p-2 rounded-lg hover:bg-slate-100 transition-colors" title="Mark as read">
                  <Eye className="w-4 h-4 text-slate-400" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
